import React, { useState, useEffect, useMemo } from 'react';
import { Select, Spin } from 'antd';
import { CustomModal } from '@/components';

const { Option } = Select;

const AddNewUsersModal = ({
  isOpen,
  onClose,
  onSave,
  allUsers = [],
  assignedUserIds = [],
  isLoadingUsers = false,
  rightOffset = 350, // Offset to position next to the AssignedUsersModal
}) => {
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Reset selection when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedUserIds([]);
      setSearchValue('');
    }
  }, [isOpen]);

  // Filter out already assigned users
  const availableUsers = useMemo(() => {
    return allUsers.filter(user => !assignedUserIds.includes(user.id));
  }, [allUsers, assignedUserIds]);

  // Filter users based on search
  const filteredUsers = useMemo(() => {
    if (!searchValue) return availableUsers;
    
    const searchLower = searchValue.toLowerCase();
    return availableUsers.filter(user => {
      const fullName = [user.first_name, user.middle_name, user.last_name]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      const team = (user.team?.name || '').toLowerCase();
      const location = (user.location?.name || '').toLowerCase();
      const email = (user.email || '').toLowerCase();
      
      return fullName.includes(searchLower) || 
             team.includes(searchLower) || 
             location.includes(searchLower) ||
             email.includes(searchLower);
    });
  }, [availableUsers, searchValue]);

  const handleSave = async () => {
    if (selectedUserIds.length === 0) {
      return;
    }

    setIsSaving(true);
    try {
      await onSave(selectedUserIds);
      onClose();
    } catch (error) {
      console.error('Failed to add users:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setSelectedUserIds([]);
    setSearchValue('');
    onClose();
  };

  // Custom render for select options
  const renderOption = (user) => {
    const fullName = [user.first_name, user.middle_name, user.last_name]
      .filter(Boolean)
      .join(' ');
    
    return (
      <div style={{ 
        padding: '4px 0',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <span style={{ 
          fontSize: '14px', 
          fontWeight: 500,
          color: '#1f2937'
        }}>
          {fullName}
        </span>
        <span style={{ 
          fontSize: '12px', 
          color: '#6b7280',
          marginTop: '2px'
        }}>
          {user?.email}
        </span>
      </div>
    );
  };

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={handleCancel}
      title="Add new users"
      subtitle="Select new users to be assigned to this role"
      primaryActionText="Save"
      secondaryActionText="Cancel"
      onPrimaryAction={handleSave}
      onSecondaryAction={handleCancel}
      width="500px"
      maxWidth="md"
      primaryActionDisabled={selectedUserIds.length === 0 || isSaving}
      loading={isSaving}
      rightOffset={rightOffset}
    >
      <div style={{ marginTop: '24px' }}>
        <label 
          style={{ 
            display: 'block',
            fontSize: '14px',
            fontWeight: 500,
            color: '#374151',
            marginBottom: '8px'
          }}
        >
          Select Users
        </label>
        
        <Select
          mode="multiple"
          placeholder="Search user..."
          value={selectedUserIds}
          onChange={setSelectedUserIds}
          onSearch={setSearchValue}
          searchValue={searchValue}
          style={{ width: '100%' }}
          showSearch
          filterOption={false}
          loading={isLoadingUsers}
          notFoundContent={
            isLoadingUsers ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '20px' 
              }}>
                <Spin size="small" />
              </div>
            ) : (
              <div style={{ 
                textAlign: 'center', 
                padding: '20px',
                color: '#9ca3af'
              }}>
                {availableUsers.length === 0 
                  ? 'All users are already assigned'
                  : 'No users found'
                }
              </div>
            )
          }
          maxTagCount="responsive"
          optionLabelProp="label"
          size="large"
        >
          {filteredUsers.map(user => {
            const fullName = [user.first_name, user.middle_name, user.last_name]
              .filter(Boolean)
              .join(' ');
            
            return (
              <Option
                key={user.id}
                value={user.id}
                label={fullName}
              >
                {renderOption(user)}
              </Option>
            );
          })}
        </Select>

        {selectedUserIds.length > 0 && (
          <div style={{ 
            marginTop: '12px',
            fontSize: '12px',
            color: '#6b7280'
          }}>
            {selectedUserIds.length} user{selectedUserIds.length !== 1 ? 's' : ''} selected
          </div>
        )}
      </div>
    </CustomModal>
  );
};

export default AddNewUsersModal;